import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity, Bot, BookOpen, Boxes, ChevronRight, CircleHelp, FolderKanban,
  Heart, House, LogOut, Menu, MessageSquareText, PackageOpen, Plug, Plus,
  Settings, Sparkles, Workflow, X,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { BrandMark } from '../components/BrandMark';
import { FloatingNavigator } from './components/FloatingNavigator';

type NavEntry = {
  to: string;
  label: string;
  caption?: string;
  icon: typeof House;
  end?: boolean;
  tour?: string;
};

type NavGroup = { label: string; items: NavEntry[] };

const NAV: NavGroup[] = [
  {
    label: 'Рабочее пространство',
    items: [
      { to: '/', label: 'Главная', caption: 'Обзор команды', icon: House, end: true, tour: 'nav-home' },
      { to: '/assistant', label: 'Ассистент', caption: 'Чаты и оркестратор', icon: Sparkles, tour: 'nav-assistant' },
      { to: '/automations', label: 'Автоматизации', caption: 'Сценарии и запуски', icon: Workflow },
      { to: '/settings/agents', label: 'Агенты', caption: 'ИИ-специалисты', icon: Bot, tour: 'nav-agents' },
    ],
  },
  {
    label: 'Данные и подключения',
    items: [
      { to: '/marketplace', label: 'Маркетплейс', caption: 'Готовые решения', icon: PackageOpen, tour: 'nav-market' },
      { to: '/settings/connections', label: 'Связи', caption: 'Сервисы, MCP, Telegram', icon: Plug },
      { to: '/settings/knowledge', label: 'База знаний', caption: 'Файлы, ссылки и текст', icon: BookOpen },
      { to: '/memory', label: 'Память', caption: 'Факты обо мне', icon: Boxes },
      { to: '/projects', label: 'Проекты', caption: 'Группы диалогов', icon: FolderKanban },
    ],
  },
  {
    label: 'Управление',
    items: [
      { to: '/settings/tasks', label: 'Задачи', caption: 'Запуски по времени', icon: Activity },
      { to: '/settings/support', label: 'Поддержать проект', icon: Heart },
      { to: '/settings/feedback', label: 'Обратная связь', icon: CircleHelp },
      { to: '/settings', label: 'Настройки', caption: 'Модели и аккаунт', icon: Settings, end: true, tour: 'nav-more' },
    ],
  },
];

const TITLES: Array<[RegExp, string, string]> = [
  [/^\/$/, 'Главная', 'Всё важное в одном месте'],
  [/^\/assistant/, 'Ассистент', 'Общайтесь, поручайте и создавайте'],
  [/^\/automation\/wizard/, 'Новая автоматизация', 'Соберите сценарий вместе с оркестратором'],
  [/^\/automation\//, 'Автоматизация', 'Настройка сценария и история запусков'],
  [/^\/automations/, 'Автоматизации', 'Ваши активные и сохранённые сценарии'],
  [/^\/settings\/agents/, 'Агенты', 'Команда ИИ-специалистов'],
  [/^\/settings\/connections/, 'Связи', 'Интеграции, MCP и Telegram-ресурсы'],
  [/^\/settings\/knowledge/, 'База знаний', 'Контекст для ассистента и агентов'],
  [/^\/settings\/tasks|^\/settings\/reminders/, 'Задачи и напоминания', 'Запланированные действия'],
  [/^\/settings\/byok/, 'Модели и API-ключи', 'Подключение собственных провайдеров'],
  [/^\/settings/, 'Настройки', 'Управление платформой'],
  [/^\/marketplace/, 'Маркетплейс', 'Готовые агенты и сценарии'],
  [/^\/memory/, 'Память', 'Что ассистент знает и запоминает'],
  [/^\/projects/, 'Проекты', 'Организация рабочих диалогов'],
  [/^\/activity/, 'Последние действия', 'Недавние изменения и запуски'],
];

function SidebarNav({ onNavigate }: { onNavigate: () => void }) {
  return (
    <nav className="oa-sidebar-nav" aria-label="Основная навигация">
      {NAV.map((group) => (
        <section className="oa-nav-group" key={group.label}>
          <h2>{group.label}</h2>
          {group.items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-tour={item.tour}
                onClick={onNavigate}
                className={({ isActive }) => `oa-nav-link${isActive ? ' is-active' : ''}`}
              >
                <span className="oa-nav-icon"><Icon size={18} strokeWidth={1.9} /></span>
                <span className="oa-nav-copy">
                  <strong>{item.label}</strong>
                  {item.caption && <small>{item.caption}</small>}
                </span>
                <ChevronRight className="oa-nav-chevron" size={14} />
              </NavLink>
            );
          })}
        </section>
      ))}
    </nav>
  );
}

export function DesktopShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const pageMeta = useMemo(() => {
    const found = TITLES.find(([pattern]) => pattern.test(location.pathname));
    return found ? { title: found[1], subtitle: found[2] } : { title: 'Очередной агент', subtitle: 'Рабочее пространство' };
  }, [location.pathname]);

  const name = user?.first_name || user?.username || `Пользователь ${user?.id ?? ''}`;
  const initial = String(name).trim().slice(0, 1).toUpperCase() || 'О';

  return (
    <div className="oa-shell">
      <button
        className={`oa-sidebar-scrim${mobileOpen ? ' is-open' : ''}`}
        aria-label="Закрыть меню"
        onClick={() => setMobileOpen(false)}
      />

      <aside className={`oa-sidebar${mobileOpen ? ' is-open' : ''}`}>
        <div className="oa-brand">
          <div className="oa-brand-mark" aria-hidden="true"><BrandMark /></div>
          <div className="oa-brand-copy">
            <strong>Очередной агент</strong>
            <span><i /> система работает</span>
          </div>
          <button className="oa-sidebar-close" onClick={() => setMobileOpen(false)} aria-label="Закрыть меню"><X size={20} /></button>
        </div>

        <SidebarNav onNavigate={() => setMobileOpen(false)} />

        <div className="oa-sidebar-footer">
          <div className="oa-profile">
            {user?.photo_url ? <img src={user.photo_url} alt="" /> : <span>{initial}</span>}
            <div><strong>{name}</strong><small>@{user?.username || `id${user?.id ?? ''}`}</small></div>
          </div>
          <button className="oa-logout" onClick={() => void logout()} title="Выйти" aria-label="Выйти"><LogOut size={17} /></button>
        </div>
      </aside>

      <div className="oa-workspace">
        <header className="oa-topbar">
          <div className="oa-topbar-title">
            <button className="oa-menu-button" onClick={() => setMobileOpen(true)} aria-label="Открыть меню"><Menu size={21} /></button>
            <div>
              <h1>{pageMeta.title}</h1>
              <p>{pageMeta.subtitle}</p>
            </div>
          </div>
          <div className="oa-topbar-actions">
            <button className="oa-quiet-action" onClick={() => navigate('/activity')}><Activity size={17} /><span>Активность</span></button>
            <button className="oa-primary-action" onClick={() => navigate('/automation/wizard')}><Plus size={18} /><span>Создать</span></button>
          </div>
        </header>

        <main className="oa-content" id="main-content">
          <div className="oa-page-stage"><Outlet /></div>
        </main>
      </div>

      <FloatingNavigator />
    </div>
  );
}
